import { useState } from "react";
import { Trans, useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import type { Bench } from "../api";

/**
 * What a rig can actually do, as its last run reported it.
 *
 * OBSERVED, NOT CONFIGURED, and the distinction is the whole point. `run.sh`
 * treats HIL_CAPS as a starting list and then probes the rig: it adds `cef`,
 * `doorclosed`/`dooropen`, `credentials`, `testca`, `newbuild` and `mains` when
 * it finds them, and drops `sivmock`, `rootshell`, `robustel`, `modem` and
 * `wd-disarmed` when it does not. So the configured value is neither what ran
 * nor a superset of it.
 *
 * Showing the configured value would have been actively misleading: on
 * 2026-08-24 sncf-lab-1 had no HIL_CAPS configured at all while every one of
 * its runs declared eighteen capabilities. The panel therefore names the run it
 * is quoting, so the number can be checked rather than believed.
 */
export default function Caps({ bench }: { bench: Bench }) {
  const { t } = useTranslation();
  const [open, setOpen] = useState<string | null>(null);
  return (
    <section className="mt-4">
      <h2 className="text-base font-semibold text-slate-800 dark:text-slate-200">
        {t("caps.heading")}
      </h2>

      {bench.caps.length === 0 ? (
        <p className="mt-2 text-sm text-slate-500">
          {/* "none reported" and "none present" are different facts and the
              second one is not knowable from here. */}
          {t("caps.none")}
        </p>
      ) : (
        <>
          {/* Compact by default, explained on demand.

              Every capability spelled out at once was eighteen paragraphs and
              pushed the rest of the bench page off a phone screen. A plain
              `title` tooltip is the obvious fix and the wrong one: there is no
              hover on a touch screen, so the explanations would vanish exactly
              where they were being read. Tapping a chip opens its line; the
              title stays for a mouse, which costs nothing and helps the case it
              does cover.

              The text comes FROM THE SERVER — a second copy here would drift,
              and the drift would read as a description of something the harness
              stopped doing months ago. */}
          <ul className="mt-2 flex flex-wrap gap-1.5">
            {bench.caps.map((c) => {
              const what = bench.caps_help?.find((h) => h.name === c)?.what;
              const isOpen = open === c;
              return (
                <li key={c}>
                  <button
                    type="button"
                    title={what}
                    aria-expanded={isOpen}
                    onClick={() => setOpen(isOpen ? null : c)}
                    className={`rounded px-2 py-0.5 font-mono text-xs ${
                      isOpen
                        ? "bg-sky-200 text-sky-950 dark:bg-sky-800 dark:text-sky-50"
                        : "bg-sky-50 text-sky-900 hover:bg-sky-100 dark:bg-sky-950 dark:text-sky-200 dark:hover:bg-sky-900"
                    }`}
                  >
                    {c}
                  </button>
                </li>
              );
            })}
          </ul>

          {open && (
            <p className="mt-2 rounded bg-slate-50 px-2 py-1.5 text-xs text-slate-600 dark:bg-slate-900 dark:text-slate-300">
              <b className="font-mono">{open}</b> —{" "}
              {bench.caps_help?.find((h) => h.name === open)?.what ??
                t("caps.noDescription")}
            </p>
          )}
          <p className="mt-1.5 text-xs text-slate-500">
            <Trans
              i18nKey="caps.reportedBy"
              count={bench.caps.length}
              values={{ run: bench.caps_from || t("caps.lastRun") }}
              components={{
                runLink: bench.caps_from ? (
                  <Link className="underline" to={`/runs?bench=${bench.id}`} />
                ) : (
                  <span />
                ),
              }}
            />
          </p>
        </>
      )}
    </section>
  );
}
